import {
  LookupTracker,
  LookupTrackerFindArg,
  LookupTrackerRegisterArg,
  ChangeMeta,
  JSONPatchDocument,
  LookupTrackerStore,
} from "../types";

export const modelToJsonPatchDocument = <T extends { [key: string]: any }>(
  updatedModel: T,
  fieldsToInclude: (keyof T)[]
): JSONPatchDocument => {
  // import { compare } from "fast-json-patch" was tried but the way it handled
  // arrays of primitive values was too focussed on the order of items, hence
  // generated lots of `replace` statements rather than `add` and `remove`

  return fieldsToInclude.map((key: keyof T) => ({
    op: "replace",
    path: `/${String(key)}`,
    value: updatedModel[key],
  }));
};

export const getLookupTracker = (
  store: LookupTrackerStore = {}
): LookupTracker => {
  const register = ({ name, id, label }: LookupTrackerRegisterArg) => {
    const processedName = String(name).replace(/\[+[0-9]\]/, "");
    const processedId = String(id);
    if (!store[processedName]) {
      store[processedName] = {};
    }
    if (!store[processedName][processedId]) {
      store[processedName][processedId] = label;
    }
  };

  const find = ({ name, id }: LookupTrackerFindArg) => {
    return store[String(name)]?.[String(id)];
  };

  return {
    register,
    find,
    raw: () => store,
  };
};

export const buildMetaData = <T>(
  newModel: T,
  oldModel: T,
  fieldsToInclude: (keyof T)[],
  lookupTracker: LookupTracker = getLookupTracker() // if no lookupTracker passed, just give ourselves an empty one
): ChangeMeta[] => {
  const clonedNewModel = clone(newModel);
  const clonedOldModel = clone(oldModel);
  const meta = fieldsToInclude.map((field) =>
    buildFieldChange(
      field as string,
      clonedNewModel,
      clonedOldModel,
      lookupTracker
    )
  );

  return meta;
};

const buildFieldChange = (
  field: string,
  newModel: any,
  oldModel: any,
  lookupTracker: LookupTracker
): ChangeMeta => {
  walkChangeObject(field, newModel, field, lookupTracker);
  walkChangeObject(field, oldModel, field, lookupTracker);

  return {
    field,
    oldValue: oldModel[field],
    newValue: newModel[field],
  };
};

const clone = (model: any) => JSON.parse(JSON.stringify(model));

const walkChangeObject = (
  rootName: string,
  parent: any,
  fieldName: string,
  lookupTracker: LookupTracker
) => {
  const value = parent[fieldName];

  if (isPrimitive(value)) {
    // we've reached a primitive value, let's see if it is a look up
    //  and mutate the parent if it is
    parent[fieldName] = String(
      lookupTracker.find({ name: rootName, id: value }) || parent[fieldName]
    );
  } else {
    // we're not quite yet at a primitive value, so recurse in to the value
    Object.keys(value).forEach((itemKey) =>
      walkChangeObject(
        getNextKey(rootName, itemKey, Array.isArray(value)),
        value,
        itemKey,
        lookupTracker
      )
    );
  }
};

const isPrimitive = (test: any) => test !== Object(test);

const getNextKey = (rootKey: string, thisKey: string, isArray: boolean) =>
  isArray ? rootKey : `${rootKey}.${thisKey}`;
